// EquiposSinMovimiento.actions.ts
import { queryClient } from '@/shared/query-client/query-client';
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { UseFetchEnabledParams, UseMutationsParams, UseUpdateParams } from "@/shared/interfaces";
import { urlBackend } from "@/shared/constants";
import { erpApi } from "@/shared/axios";
import { getUrlParamsFetch } from "@/shared/utils";
import { toApiError } from "@/shared/utils/general";
import { EquiposSinMovimiento, EquiposSinMovimientoForm, EquiposSinMovimientoParams, MaterialesSinMovimiento, MaterialesSinMovimientoParams } from '@/erp/reportes/equipos-sin-movimiento/types/equipos-sin-movimiento.interface';

const { get, post, put } = erpApi();

// get

export const useFetchGetEquiposSinMovimiento = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<EquiposSinMovimientoParams> = {}) => {
  return useQuery({
    queryKey: ['equipos-sin-movimiento', ...Object.values(params ?? {})],
    queryFn: () => getEquiposSinMovimiento(params),
    enabled: enabled,
    refetchOnWindowFocus: false,
  });
};

export const getEquiposSinMovimiento = async (
  params?: EquiposSinMovimientoParams
) => {
  const queryParams = getUrlParamsFetch(params ?? {});
  return get<EquiposSinMovimiento[]>(`${urlBackend}api/v1/equipos-sin-movimiento/?${queryParams}`, true);
};

export const useFetchGetMaterialesSinMovimiento = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<MaterialesSinMovimientoParams> = {}) => {
  return useQuery({
    queryKey: ['materiales-sin-movimiento', ...Object.values(params ?? {})],
    queryFn: () => getMaterialesSinMovimiento(params),
    enabled: enabled,
    refetchOnWindowFocus: false,
  });
};

export const getMaterialesSinMovimiento = async (
  params?: MaterialesSinMovimientoParams
) => {
  const queryParams = getUrlParamsFetch(params ?? {});
  return get<MaterialesSinMovimiento[]>(`${urlBackend}api/v1/materiales-sin-movimiento/?${queryParams}`, true);
};

// create

export const useFetchPostEquiposSinMovimiento = ({
  returnUrl,
  navigate
}: UseMutationsParams = {}) => {

  return useMutation({
    mutationFn: (data: EquiposSinMovimientoForm) => {
      return postEquiposSinMovimiento(data)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['equipos-sin-movimiento']
      });

      navigate && returnUrl && navigate(returnUrl);

      toast.success(data.message);

    },
    onError: ((error) => {
      const data = toApiError(error)
      toast.error(data.message)
    }),
    onSettled: () => {

    },
  });
};

export const postEquiposSinMovimiento = async (data: EquiposSinMovimientoForm) => {
  return await post<EquiposSinMovimiento>(`${urlBackend}api/v1/equipos-sin-movimiento/`, data, true);
};

// filter by id

export const useFetchGetByIdEquiposSinMovimiento = (id: string) => {
  return useQuery({
    queryKey: ['equipos-sin-movimiento', id],
    queryFn: async () => {
      return getByIdEquiposSinMovimiento(id);
    },
    refetchOnWindowFocus: false,
  });
};

export const getByIdEquiposSinMovimiento = async (id: string) => {
  const res = await get<EquiposSinMovimiento>(`${urlBackend}api/v1/equipos-sin-movimiento/${id}`, true);
  return res;
};

// update

export const useFetchPutEquiposSinMovimiento = ({
  navigate,
  returnUrl
}: UseMutationsParams = {}) => {

  return useMutation({
    mutationFn: (data: UseUpdateParams<EquiposSinMovimientoForm>) => {
      return putEquiposSinMovimiento(data);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: ['equipos-sin-movimiento']
      });
      
      navigate && returnUrl && navigate(returnUrl);

      toast.success(res.message);
    },
    onError: (error) => {
      const data = toApiError(error)
      toast.error(data.message)
    },
    onSettled: () => {

    },
  });
};

export const putEquiposSinMovimiento = async ({
  id,
  data
}: UseUpdateParams<EquiposSinMovimientoForm>) => {
  return put<EquiposSinMovimiento>(`${urlBackend}api/v1/equipos-sin-movimiento/${id}/`, data, true);
};
